import type { MessageId } from "../i18n/messages"
import type { useI18n } from "../i18n/useI18n"

export type ArtworkErrorCode =
  | "ARTWORK_UNSUPPORTED_MIME_TYPE"
  | "ARTWORK_UNSUPPORTED_EXTENSION"
  | "ARTWORK_FILE_TOO_LARGE"
  | "ARTWORK_INVALID_DIMENSIONS"
  | "ARTWORK_INVALID_UPLOAD"
  | "ARTWORK_PROCESSING_NO_VARIANTS"
  | "ARTWORK_DECODE_FAILED"
  | "ARTWORK_HEIC_UNSUPPORTED"
  | "ARTWORK_CANVAS_UNAVAILABLE"
  | "ARTWORK_SOURCE_UNAVAILABLE"
  | "ARTWORK_ENCODE_FAILED"
  | "ARTWORK_READ_FAILED"
  | "ARTWORK_DATA_URL_FAILED"
  | "ARTWORK_SYNC_NO_WORKER"
  | "ARTWORK_SYNC_FAILED"
  | "ARTWORK_TURNSTILE_FAILED"
  | "ARTWORK_TURNSTILE_REQUIRED"
  | "ARTWORK_TURNSTILE_UNAVAILABLE"

const ARTWORK_ERROR_MESSAGE_IDS: Record<ArtworkErrorCode, MessageId> = {
  ARTWORK_CANVAS_UNAVAILABLE: "ui.artwork.error.canvasUnavailable",
  ARTWORK_DATA_URL_FAILED: "ui.artwork.error.dataUrlFailed",
  ARTWORK_DECODE_FAILED: "ui.artwork.error.decodeFailed",
  ARTWORK_HEIC_UNSUPPORTED: "ui.artwork.error.heicUnsupported",
  ARTWORK_ENCODE_FAILED: "ui.artwork.error.encodeFailed",
  ARTWORK_FILE_TOO_LARGE: "ui.artwork.error.fileTooLarge",
  ARTWORK_INVALID_DIMENSIONS: "ui.artwork.error.invalidDimensions",
  ARTWORK_INVALID_UPLOAD: "ui.artwork.error.invalidUpload",
  ARTWORK_PROCESSING_NO_VARIANTS: "ui.artwork.error.noVariants",
  ARTWORK_READ_FAILED: "ui.artwork.error.readFailed",
  ARTWORK_SOURCE_UNAVAILABLE: "ui.artwork.error.sourceUnavailable",
  ARTWORK_SYNC_FAILED: "ui.artwork.error.syncFailed",
  ARTWORK_SYNC_NO_WORKER: "ui.artwork.error.syncUnavailable",
  ARTWORK_TURNSTILE_FAILED: "ui.artwork.error.turnstileFailed",
  ARTWORK_TURNSTILE_REQUIRED: "ui.artwork.error.turnstileRequired",
  ARTWORK_TURNSTILE_UNAVAILABLE: "ui.artwork.error.turnstileUnavailable",
  ARTWORK_UNSUPPORTED_EXTENSION: "ui.artwork.error.unsupportedExtension",
  ARTWORK_UNSUPPORTED_MIME_TYPE: "ui.artwork.error.unsupportedMimeType",
}

export class ArtworkError extends Error {
  readonly code: ArtworkErrorCode

  constructor(code: ArtworkErrorCode, message?: string) {
    super(message ?? code)
    this.code = code
    this.name = "ArtworkError"
  }
}

export function getArtworkErrorCode(error: unknown): ArtworkErrorCode | null {
  return error instanceof ArtworkError ? error.code : null
}

export function getArtworkErrorLabel(
  t: ReturnType<typeof useI18n>["t"],
  error: unknown,
): string | null {
  const code = getArtworkErrorCode(error)
  if (code) return t(ARTWORK_ERROR_MESSAGE_IDS[code])
  return error instanceof Error ? error.message : null
}
